import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ROLES,
  ROLE_LABELS,
  type DashboardUser,
  type Permission,
  type Role,
} from "@xop/dashboard-shared";
import { Camera, KeyRound, MailPlus, MoreHorizontal, Pencil, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { ProfileCardTrigger } from "../../../components/ProfileCardTrigger.js";
import { UserAvatar } from "../../../components/UserAvatar.js";
import { formatClient } from "./formatClient.js";
import { PermissionEditor } from "./PermissionEditor.js";
import { rowSelectSx } from "./tableStyles.js";

export function UserRow({
  user,
  canManagePermissions,
  canDelete,
  onRoleChange,
  onPermissionsChange,
  onRename,
  onSetPassword,
  onResetTwoFactor,
  onSendInvite,
  onSetAvatar,
  onDelete,
}: {
  user: DashboardUser;
  canManagePermissions: boolean;
  canDelete: boolean;
  onRoleChange: (role: Role) => void;
  onPermissionsChange: (permissions: Permission[]) => void;
  onRename: () => void;
  onSetPassword: () => void;
  onResetTwoFactor: (method: "email" | "disabled") => void;
  onSendInvite: () => void;
  onSetAvatar: () => void;
  onDelete: () => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeMenu = (): void => setMenuAnchor(null);

  return (
    <>
      <TableRow {...(expanded ? { sx: { "& td": { borderBottom: 0 } } } : {})}>
        <TableCell>
          <ProfileCardTrigger userId={user.id} name={user.name}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <UserAvatar userId={user.id} name={user.name} size={32} />
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Box sx={{ fontWeight: 500, color: "text.primary" }}>{user.name}</Box>
                {user.name !== user.email && (
                  <Box sx={{ fontSize: 12, color: "text.disabled" }}>{user.email}</Box>
                )}
              </Box>
            </Box>
          </ProfileCardTrigger>
        </TableCell>
        <TableCell>
          <Box
            component="select"
            value={user.dashboard_role}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onRoleChange(e.target.value as Role)}
            aria-label={`Dashboard role for ${user.email}`}
            sx={rowSelectSx}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </Box>
        </TableCell>
        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
          {user.permissions.length} granted
          {canManagePermissions && (
            <Button
              variant="text"
              color="inherit"
              size="small"
              sx={{ ml: 1 }}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? "Hide" : "Edit"}
            </Button>
          )}
        </TableCell>
        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
          <Box component="span" title={formatClient(user.client_version).title}>
            {formatClient(user.client_version).label}
          </Box>
        </TableCell>
        <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>
          {user.last_seen_at
            ? new Date(user.last_seen_at).toLocaleString()
            : "—"}
        </TableCell>
        <TableCell sx={{ textAlign: "right" }}>
          <IconButton
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            title={`Manage ${user.name}`}
            aria-label={`Manage ${user.email}`}
            sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
          >
            <MoreHorizontal size={16} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu}>
            <MenuItem
              onClick={() => {
                closeMenu();
                onSendInvite();
              }}
              disabled={!canDelete}
            >
              <ListItemIcon>
                <MailPlus size={16} />
              </ListItemIcon>
              <ListItemText>
                {canDelete ? "Send login invite…" : "Send login invite (use your profile)"}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onRename();
              }}
            >
              <ListItemIcon>
                <Pencil size={16} />
              </ListItemIcon>
              <ListItemText>Rename…</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onSetAvatar();
              }}
            >
              <ListItemIcon>
                <Camera size={16} />
              </ListItemIcon>
              <ListItemText>Set photo…</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onSetPassword();
              }}
              disabled={!canDelete}
            >
              <ListItemIcon>
                <KeyRound size={16} />
              </ListItemIcon>
              <ListItemText>{canDelete ? "Set password…" : "Set password (use your profile)"}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onResetTwoFactor("email");
              }}
              disabled={!canDelete}
            >
              <ListItemIcon>
                <ShieldCheck size={16} />
              </ListItemIcon>
              <ListItemText>
                {canDelete ? "Reset 2FA to email…" : "Reset 2FA (use your profile)"}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                onResetTwoFactor("disabled");
              }}
              disabled={!canDelete}
            >
              <ListItemIcon>
                <ShieldOff size={16} />
              </ListItemIcon>
              <ListItemText>Reset 2FA (force re-enroll)…</ListItemText>
            </MenuItem>
          </Menu>
          <IconButton
            onClick={onDelete}
            disabled={!canDelete}
            title={
              canDelete
                ? `Delete dashboard access for ${user.email}`
                : "You cannot delete your own account"
            }
            aria-label={`Delete dashboard access for ${user.email}`}
            sx={{
              color: "text.disabled",
              "&:hover": {
                bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                color: "error.main",
              },
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </TableCell>
      </TableRow>
      {expanded && canManagePermissions && (
        <TableRow>
          <TableCell colSpan={6} sx={{ bgcolor: "surfaceSoft" }}>
            <PermissionEditor
              current={user.permissions}
              onChange={onPermissionsChange}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
